// Contatti: messaggio all'artigiano tramite Rarifex, social e località
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Globe, MapPin, MessageSquare, Sparkles } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { socialHref } from '../ShopFooter';
import { useShop } from '../ShopContext';
import ShopLocation from '../ShopLocation';
import { PageHeader, PrimaryButton, Section } from '../ShopUi';

const LABELS = { website: 'Sito web', instagram: 'Instagram', facebook: 'Facebook', tiktok: 'TikTok', youtube: 'YouTube', pinterest: 'Pinterest', linkedin: 'LinkedIn' };

export default function ShopContact() {
  const { crafter, name, hasSocial, menuKeys, openRequest, style, branding } = useShop();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const startChat = async () => {
    if (!user) {
      toast.info('Accedi o registrati su Rarifex per scrivere all\'artigiano');
      navigate('/login');
      return;
    }
    if (user.id === crafter.id) {
      toast.error('Questa è la tua bottega');
      return;
    }
    setBusy(true);
    try {
      const { data: existing } = await supabase
        .from('conversations').select('id')
        .or(`and(participant_1.eq.${user.id},participant_2.eq.${crafter.id}),and(participant_1.eq.${crafter.id},participant_2.eq.${user.id})`)
        .maybeSingle();
      if (existing) {
        navigate(`/messages?conversation=${existing.id}`);
        return;
      }
      const { data: conv, error } = await supabase
        .from('conversations').insert({ participant_1: user.id, participant_2: crafter.id }).select().single();
      if (error) throw error;
      navigate(`/messages?conversation=${conv.id}`);
    } catch (e) {
      toast.error('Non riusciamo ad aprire la conversazione, riprova');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="shop-contact">
      <PageHeader title="Contatti" subtitle={`Scrivi a ${name}: rispondo personalmente a ogni messaggio.`} />
      <Section>
        <div className="grid md:grid-cols-2 gap-8">
          <div className={`p-6 ${style.panel}`}>
            <h2 className="font-semibold text-slate-900 flex items-center gap-2"><MessageSquare className="w-5 h-5 text-shop-primary" /> Scrivimi un messaggio</h2>
            <p className="mt-2 text-sm text-slate-600">I messaggi passano dalla chat di Rarifex: restano collegati ai tuoi ordini e sono protetti.</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <PrimaryButton onClick={startChat} disabled={busy} data-testid="shop-contact-chat">Apri la chat</PrimaryButton>
              {menuKeys.includes('su_misura') && (
                <button type="button" onClick={openRequest} className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-shop-primary text-shop-primary font-medium hover:bg-shop-primary/5">
                  <Sparkles className="w-4 h-4" /> Richiesta su misura
                </button>
              )}
            </div>
          </div>
          <div className="space-y-4">
            {!branding.contact && crafter.city && (
              <p className="flex items-center gap-2 text-slate-700"><MapPin className="w-5 h-5 text-shop-primary" /> {crafter.city}{crafter.region ? `, ${crafter.region}` : ''}</p>
            )}
            {hasSocial && (
              <div className="flex flex-wrap gap-2">
                {Object.entries(crafter.social_links).filter(([, v]) => v).map(([k, v]) => (
                  <a key={k} href={socialHref(v)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-300 bg-white text-sm hover:border-shop-primary hover:text-shop-primary">
                    {k === 'website' && <Globe className="w-4 h-4" />} {LABELS[k] || k}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </Section>
      {branding.contact && (
        <Section title={branding.contact.visits !== 'none' ? 'Vieni a trovarci in bottega' : 'Dove siamo'} className="!pt-0">
          <ShopLocation />
        </Section>
      )}
    </div>
  );
}
